// src/components/RealTripAnalysisWidget.tsx
import React from 'react';
import { Box, Typography, Divider } from '@mui/material';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';

interface Props {
  data: any[];
}

const RealTripAnalysisWidget: React.FC<Props> = ({ data }) => {
  // ✅ ADD DEBUG LOGS
  console.log('🚗 Trip Analysis Raw Data:', {
    dataLength: data?.length,
    allData: data,
    firstItem: data?.[0],
    firstItemDevice: data?.[0]?.device,
    firstItemDeviceId: data?.[0]?.deviceId
  });

  if (!data || data.length === 0) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" height="100%">
        <Typography color="textSecondary">No trip data available</Typography>
      </Box>
    );
  }

  // Calculate totals
  const totalTrips = data.reduce((sum, d) => sum + (d.tripCount || 0), 0);
  const totalDistance = data.reduce((sum, d) => sum + (d.totalDistance || 0), 0);
  const totalDuration = data.reduce((sum, d) => sum + (d.totalDurationMinutes || 0), 0);

  // Top 5 most active vehicles - ✅ FIXED: Use plateNumber with fallback
  const topVehicles = data
    .sort((a, b) => (b.tripCount || 0) - (a.tripCount || 0))
    .slice(0, 5)
    .map(item => ({
      name: item.device?.plateNumber || item.device?.imei || `Device ${item.deviceId?.toString().slice(-4)}`,
      value: item.tripCount || 0,
      fullName: item.device?.plateNumber || 'Unknown Vehicle',
      rawDevice: item.device  // ✅ ADD THIS FOR DEBUGGING
    }));

  // ✅ ADD DEBUG LOG FOR PROCESSED DATA
  console.log('🚗 Top Vehicles for Chart:', topVehicles);

  const COLORS = ['#2196f3', '#4caf50', '#ff9800', '#f44336', '#9c27b0'];

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Summary Stats */}
      <Box sx={{ mb: 2 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
          {totalTrips}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Total Trips • {totalDistance.toFixed(1)} km • {Math.round(totalDuration / 60)} hrs
        </Typography>
      </Box>

      {/* Trip Distribution Chart */}
      <Box sx={{ flexGrow: 1, minHeight: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={topVehicles}
              cx="50%"
              cy="50%"
              labelLine={false}
              outerRadius={80}
              fill="#8884d8"
              dataKey="value"
              label={({ fullName, percent }) => 
                `${fullName}: ${(percent * 100).toFixed(0)}%`
              }
            >
              {topVehicles.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip 
              formatter={(value: any, name: any, props: any) => [
                `${value} trips`,
                props.payload.fullName
              ]}
            />
          </PieChart>
        </ResponsiveContainer>
      </Box>

      {/* Details List */}
      <Box sx={{ mt: 2 }}>
        <Divider sx={{ mb: 1 }} />
        <Typography variant="caption" color="textSecondary">
          Avg trip: {totalTrips > 0 ? Math.round(totalDistance / totalTrips) : 0} km, {totalTrips > 0 ? Math.round(totalDuration / totalTrips) : 0} min
        </Typography>
      </Box>
    </Box>
  );
};

export default RealTripAnalysisWidget;